import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";

const VideoManager = () => {
  const [title, setTitle] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [category, setCategory] = useState("");

  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
const [videoSearch, setVideoSearch] = useState("");
const [currentPage, setCurrentPage] = useState(1);
const videosPerPage = 50;
  // =====================================================
  // YOUTUBE THUMBNAIL
  // =====================================================

  const getYoutubeThumbnail = (url) => {
    if (!url) return "";

    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([^&?/]+)/
    );

    if (!match) return "";

    return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
  };

  // =====================================================
  // LOAD CATEGORIES
  // =====================================================

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, "categories")
        );

        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort(
            (a, b) =>
              Number(a.order || 0) -
              Number(b.order || 0)
          );

        setCategories(data);

        if (data.length > 0) {
          setCategory(data[0].name);
        }
      } catch (error) {
        console.error(
          "Category load error:",
          error
        );
      }
    };

    fetchCategories();
  }, []);

  // =====================================================
  // LOAD VIDEOS
  // =====================================================

  const fetchVideos = async () => {
    try {
      const q = query(
        collection(db, "videos"),
        orderBy("timestamp", "desc")
      );

      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setVideos(data);
    } catch (error) {
      console.error(
        "Video load error:",
        error
      );
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // =====================================================
  // ADD / UPDATE VIDEO
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert("ভিডিওর শিরোনাম দিন!");
      return;
    }

    if (!youtubeUrl.trim()) {
      alert("YouTube ভিডিওর লিংক দিন!");
      return;
    }

    if (!category) {
      alert("ক্যাটাগরি নির্বাচন করুন!");
      return;
    }

    const thumbnail =
      getYoutubeThumbnail(
        youtubeUrl.trim()
      );

    if (!thumbnail) {
      alert(
        "সঠিক YouTube ভিডিও লিংক দিন!"
      );
      return;
    }

    setLoading(true);

    try {
      // ==========================
      // UPDATE
      // ==========================

      if (editingId) {
        await updateDoc(
          doc(db, "videos", editingId),
          {
            title: title.trim(),
            youtubeUrl: youtubeUrl.trim(),
            category: category,
            thumbnail: thumbnail,
          }
        );

        alert("ভিডিও সফলভাবে আপডেট হয়েছে!");

        setEditingId(null);
      }

      // ==========================
      // ADD NEW
      // ==========================

      else {
        await addDoc(
          collection(db, "videos"),
          {
            title: title.trim(),
            youtubeUrl: youtubeUrl.trim(),
            category: category,
            thumbnail: thumbnail,
            status: "active",
            timestamp: serverTimestamp(),
          }
        );

        alert("ভিডিও সফলভাবে প্রকাশিত হয়েছে!");
      }

      // Form reset
      setTitle("");
      setYoutubeUrl("");

      if (categories.length > 0) {
        setCategory(categories[0].name);
      }

      await fetchVideos();
    } catch (error) {
      console.error(
        "Video save error:",
        error
      );

      alert(
        editingId
          ? "ভিডিও আপডেট করতে সমস্যা হয়েছে!"
          : "ভিডিও প্রকাশ করতে সমস্যা হয়েছে!"
      );
    }

    setLoading(false);
  };

  // =====================================================
  // EDIT
  // =====================================================

  const handleEdit = (video) => {
    setEditingId(video.id);
    setTitle(video.title || "");
    setYoutubeUrl(video.youtubeUrl || "");
    setCategory(video.category || "");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // CANCEL EDIT
  // =====================================================

  const handleCancelEdit = () => {
    setEditingId(null);
    setTitle("");
    setYoutubeUrl("");

    if (categories.length > 0) {
      setCategory(categories[0].name);
    }
  };

  // =====================================================
  // DELETE
  // =====================================================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "আপনি কি এই ভিডিওটি ডিলিট করতে চান?"
    );

    if (!confirmDelete) return;

    try {
      await deleteDoc(
        doc(db, "videos", id)
      );

      setVideos((prev) =>
        prev.filter(
          (video) => video.id !== id
        )
      );

      alert("ভিডিও ডিলিট হয়েছে!");
    } catch (error) {
      console.error(
        "Delete error:",
        error
      );

      alert(
        "ভিডিও ডিলিট করতে সমস্যা হয়েছে!"
      );
    }
  };

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";

    const date = timestamp.toDate
      ? timestamp.toDate()
      : new Date(timestamp);

    return date.toLocaleDateString(
      "bn-BD",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";

    const date = timestamp.toDate
      ? timestamp.toDate()
      : new Date(timestamp);

    return date.toLocaleTimeString(
      "bn-BD",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  // =====================================================
  // BUTTON STYLE
  // =====================================================

  const actionButtonStyle = (
    type,
    isHovered
  ) => ({
    padding: "7px 13px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "13px",
    background:
      type === "edit"
        ? isHovered
          ? "#4169E1"
          : "#666"
        : isHovered
        ? "#8b0000"
        : "#666",
    transition: "0.2s",
    marginRight:
      type === "edit" ? "6px" : "0",
  });

const filteredVideos = videos.filter((video) => {
  const search = videoSearch.trim().toLowerCase();

  if (!search) return true;

  return (
    video.title?.toLowerCase().includes(search) ||
    video.category?.toLowerCase().includes(search)
  );
});

const totalPages = Math.ceil(
  filteredVideos.length / videosPerPage
);

const indexOfLastVideo =
  currentPage * videosPerPage;

const indexOfFirstVideo =
  indexOfLastVideo - videosPerPage;

const currentVideos = filteredVideos.slice(
  indexOfFirstVideo,
  indexOfLastVideo
);

  return (
    <div
      style={{
        padding: "10px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >

      {/* =================================================
          ADD / EDIT FORM
      ================================================= */}

      <div
        style={{
          background: "#fafafa",
          border: "1px solid #ddd",
          borderRadius: "6px",
          padding: "18px",
          marginBottom: "30px",
        }}
      >

        <h4
          style={{
            margin: "0 0 15px",
            borderLeft:
              "5px solid #d2202a",
            paddingLeft: "10px",
          }}
        >
          {editingId
            ? "ভিডিও সম্পাদনা"
            : "নতুন ভিডিও প্রকাশ"}
        </h4>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "grid",
            gridTemplateColumns:
              "1fr 1fr",
            gap: "15px",
          }}
        >

          {/* TITLE */}

          <input
            type="text"
            placeholder="ভিডিওর শিরোনাম"
            value={title}
            onChange={(e) =>
              setTitle(e.target.value)
            }
            style={{
              padding: "12px",
              fontSize: "16px",
              border:
                "1px solid #ccc",
              borderRadius: "4px",
              boxSizing:
                "border-box",
              width: "100%",
            }}
          />

          {/* YOUTUBE URL */}

          <input
            type="text"
            placeholder="YouTube ভিডিও লিংক"
            value={youtubeUrl}
            onChange={(e) =>
              setYoutubeUrl(
                e.target.value
              )
            }
            style={{
              padding: "12px",
              fontSize: "16px",
              border:
                "1px solid #ccc",
              borderRadius: "4px",
              boxSizing:
                "border-box",
              width: "100%",
            }}
          />

          {/* CATEGORY */}

          <select
            value={category}
            onChange={(e) =>
              setCategory(e.target.value)
            }
            style={{
              padding: "12px",
              fontSize: "16px",
              border:
                "1px solid #ccc",
              borderRadius: "4px",
              background: "#fff",
            }}
          >
            {categories.map((cat) => (
              <option
                key={cat.id}
                value={cat.name}
              >
                {cat.name}
              </option>
            ))}
          </select>

          {/* BUTTONS */}

          <div
            style={{
              display: "flex",
              gap: "8px",
            }}
          >

            <button
              type="submit"
              disabled={loading}
              onMouseEnter={() =>
                setHoveredButton("save")
              }
              onMouseLeave={() =>
                setHoveredButton(null)
              }
              style={{
                flex: 1,
                padding: "12px",
                background:
                  loading
                    ? "#999"
                    : hoveredButton ===
                      "save"
                    ? "#4169E1"
                    : "#666",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: loading
                  ? "not-allowed"
                  : "pointer",
                transition:
                  "0.2s",
              }}
            >
              {loading
                ? editingId
                  ? "আপডেট হচ্ছে..."
                  : "প্রকাশ হচ্ছে..."
                : editingId
                ? "আপডেট করুন"
                : "ভিডিও প্রকাশ করুন"}
            </button>

            {editingId && (
            <button
  type="button"
  onClick={handleCancelEdit}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = "#8b0000";
    e.currentTarget.style.transform = "translateY(-1px)";
    e.currentTarget.style.boxShadow =
      "0 3px 8px rgba(139,0,0,0.25)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = "#666";
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "none";
  }}
  style={{
    padding: "12px 18px",
    background: "#666",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    transition: "all 0.2s ease",
  }}
>
  বাতিল
</button>
            )}

          </div>
        </form>
      </div>

     {/* =================================================
    PUBLISHED VIDEO LIST
================================================= */}

<div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "15px",
    marginBottom: "15px",
    flexWrap: "wrap",
  }}
>
  <h4
    style={{
      borderLeft: "5px solid #d2202a",
      paddingLeft: "10px",
      margin: 0,
      fontSize: "18px",
    }}
  >
    প্রকাশিত ভিডিও
  </h4>

  {/* SEARCH */}
  <div
    style={{
      position: "relative",
      width: "280px",
      maxWidth: "100%",
    }}
  >
    <input
      type="text"
      value={videoSearch}
    onChange={(e) => {
  setVideoSearch(e.target.value);
  setCurrentPage(1);
}}
      placeholder="ভিডিও খুঁজুন..."
      style={{
        width: "100%",
        padding: "9px 40px 9px 12px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        fontSize: "14px",
        outline: "none",
        boxSizing: "border-box",
        transition: "all 0.2s ease",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "#d2202a";
        e.currentTarget.style.boxShadow =
          "0 0 0 2px rgba(210,32,42,0.08)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "#ccc";
        e.currentTarget.style.boxShadow = "none";
      }}
    />

    <span
      style={{
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#777",
        fontSize: "16px",
        pointerEvents: "none",
      }}
    >
      🔍
    </span>
  </div>
</div>

      {filteredVideos.length === 0 ? (
  <div
    style={{
      padding: "30px",
      textAlign: "center",
      color: "#777",
      border: "1px solid #eee",
    }}
  >
    {videoSearch
      ? `"${videoSearch}" নামে কোনো ভিডিও পাওয়া যায়নি।`
      : "কোনো ভিডিও পাওয়া যায়নি।"}
  </div>
      ) : (

        <div
          style={{
            width: "100%",
            overflowX: "auto",
            border:
              "1px solid #ddd",
            borderRadius: "6px",
          }}
        >

          <table
            style={{
              width: "100%",
              minWidth: "850px",
              borderCollapse:
                "collapse",
              background: "#fff",
            }}
          >

            <thead>
              <tr
                style={{
                  background:
                    "#f5f5f5",
                  borderBottom:
                    "2px solid #ddd",
                }}
              >
                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "center",
                    width: "55px",
                  }}
                >
                  SL
                </th>

                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                    width: "145px",
                  }}
                >
                  প্রকাশের তারিখ
                </th>

                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                  }}
                >
                  শিরোনাম
                </th>

                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "center",
                    width: "150px",
                  }}
                >
                  Thumbnail
                </th>

                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                    width: "120px",
                  }}
                >
                  Category
                </th>

                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "center",
                    width: "150px",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>

            <tbody>

          {currentVideos.map((video, index) => (
  <tr
    key={video.id}
    style={{
      borderBottom: "1px solid #eee",
    }}
  >

                    {/* SL */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                        fontWeight:
                          "bold",
                        color:
                          "#555",
                      }}
                    >
                      {indexOfFirstVideo + index + 1}
                    </td>

                    {/* DATE */}

                    <td
                      style={{
                        padding:
                          "10px",
                        color:
                          "#555",
                        fontSize:
                          "13px",
                        verticalAlign:
                          "middle",
                      }}
                    >
                      <div>
                        {formatDate(
                          video.timestamp
                        )}
                      </div>

                      <div
                        style={{
                          color:
                            "#999",
                          marginTop:
                            "3px",
                        }}
                      >
                        {formatTime(
                          video.timestamp
                        )}
                      </div>
                    </td>

                    {/* TITLE */}

                    <td
                      style={{
                        padding:
                          "10px",
                        verticalAlign:
                          "middle",
                      }}
                    >
                      <div
                        style={{
                          fontSize:
                            "16px",
                          fontWeight:
                            "bold",
                          lineHeight:
                            "1.5",
                          color:
                            "#222",
                        }}
                      >
                        {video.title}
                      </div>
                    </td>

                    {/* THUMBNAIL */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                      }}
                    >
                      <img
                        src={
                          video.thumbnail ||
                          getYoutubeThumbnail(
                            video.youtubeUrl
                          )
                        }
                        alt={
                          video.title
                        }
                        style={{
                          width:
                            "120px",
                          height:
                            "68px",
                          objectFit:
                            "cover",
                          borderRadius:
                            "4px",
                          display:
                            "block",
                          margin:
                            "0 auto",
                        }}
                      />
                    </td>

                    {/* CATEGORY */}

                    <td
                      style={{
                        padding:
                          "10px",
                        verticalAlign:
                          "middle",
                      }}
                    >
                      <span
                        style={{
                          display:
                            "inline-block",
                          padding:
                            "5px 9px",
                          background:
                            "#f5f5f5",
                          borderRadius:
                            "4px",
                          fontSize:
                            "13px",
                          fontWeight:
                            "bold",
                          color:
                            "#555",
                        }}
                      >
                        {video.category ||
                          "—"}
                      </span>
                    </td>

                    {/* ACTION */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                        whiteSpace:
                          "nowrap",
                      }}
                    >

                      <button
                        type="button"
                        onClick={() =>
                          handleEdit(
                            video
                          )
                        }
                        onMouseEnter={() =>
                          setHoveredButton(
                            `edit-${video.id}`
                          )
                        }
                        onMouseLeave={() =>
                          setHoveredButton(
                            null
                          )
                        }
                        style={actionButtonStyle(
                          "edit",
                          hoveredButton ===
                            `edit-${video.id}`
                        )}
                      >
                        ✏️ Edit
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleDelete(
                            video.id
                          )
                        }
                        onMouseEnter={() =>
                          setHoveredButton(
                            `delete-${video.id}`
                          )
                        }
                        onMouseLeave={() =>
                          setHoveredButton(
                            null
                          )
                        }
                        style={actionButtonStyle(
                          "delete",
                          hoveredButton ===
                            `delete-${video.id}`
                        )}
                      >
                        🗑️ Delete
                      </button>

                    </td>

                  </tr>
                ))}

            </tbody>
          </table>
		  {totalPages > 1 && (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "6px",
      marginTop: "20px",
      flexWrap: "wrap",
    }}
  >
    {/* Previous */}
    <button
      type="button"
      disabled={currentPage === 1}
      onClick={() =>
        setCurrentPage((prev) => prev - 1)
      }
      style={{
        padding: "8px 12px",
        border: "1px solid #ddd",
        borderRadius: "4px",
        background:
          currentPage === 1 ? "#eee" : "#666",
        color: "#fff",
        cursor:
          currentPage === 1
            ? "not-allowed"
            : "pointer",
        fontWeight: "bold",
      }}
    >
      ‹
    </button>

    {/* Page Numbers */}
    {Array.from(
      { length: totalPages },
      (_, index) => index + 1
    ).map((page) => (
      <button
        key={page}
        type="button"
        onClick={() => setCurrentPage(page)}
        onMouseEnter={(e) => {
          if (currentPage !== page) {
            e.currentTarget.style.background =
              "#d2202a";
            e.currentTarget.style.color = "#fff";
          }
        }}
        onMouseLeave={(e) => {
          if (currentPage !== page) {
            e.currentTarget.style.background =
              "#fff";
            e.currentTarget.style.color = "#333";
          }
        }}
        style={{
          minWidth: "38px",
          padding: "8px 10px",
          border: "1px solid #ddd",
          borderRadius: "4px",
          background:
            currentPage === page
              ? "#d2202a"
              : "#fff",
          color:
            currentPage === page
              ? "#fff"
              : "#333",
          cursor: "pointer",
          fontWeight: "bold",
          transition: "0.2s",
        }}
      >
        {page}
      </button>
    ))}

    {/* Next */}
    <button
      type="button"
      disabled={currentPage === totalPages}
      onClick={() =>
        setCurrentPage((prev) => prev + 1)
      }
      style={{
        padding: "8px 12px",
        border: "1px solid #ddd",
        borderRadius: "4px",
        background:
          currentPage === totalPages
            ? "#eee"
            : "#666",
        color: "#fff",
        cursor:
          currentPage === totalPages
            ? "not-allowed"
            : "pointer",
        fontWeight: "bold",
      }}
    >
      ›
    </button>
  </div>
)}
        </div>
      )}

      {/* =================================================
          MOBILE
      ================================================= */}

      <style>{`
        @media (max-width: 700px) {

          .video-manager-form {
            grid-template-columns: 1fr !important;
          }

        }
      `}</style>

    </div>
  );
};

export default VideoManager;